import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import { FormUploader } from '@/components/ui/form-fields/form-uploader';
import { Input } from '@/components/ui/input';
import { useDynamicList } from '@/services/dynamic-resource';

export function needsTransitionModal(status: string) {
  return status === 'confirmed' || status === 'delivered' || status === 'canceled';
}

interface StatusTransitionModalProps {
  status: string | null;
  sale: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (extra: Record<string, string>) => void;
}

interface AttachmentValues {
  invoicePdfPath?: string;
  invoiceXmlPath?: string;
}

/**
 * Modal genérico pros dados extra exigidos por algumas transições de status no Kanban:
 * 'confirmado' pede a data de pagamento da entrada; 'entregue' pede a data do saldo e,
 * se o pedido tiver a opção 'Emitir Nota Fiscal' marcada, o número da nota (obrigatório)
 * e os anexos de PDF/XML (opcionais, via o mesmo campo de upload genérico das screens).
 */
export function StatusTransitionModal({
  status,
  sale,
  open,
  onOpenChange,
  onConfirm,
}: StatusTransitionModalProps) {
  const [depositPaidAt, setDepositPaidAt] = useState('');
  const [balancePaidAt, setBalancePaidAt] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const attachmentsForm = useForm<AttachmentValues>({ defaultValues: {} });

  const { data: optionsResponse } = useDynamicList('additional-options', {
    scope: 'sale',
    perPage: 100,
  });
  const invoiceOption = (optionsResponse?.data ?? []).find(
    (o: any) => o.slug === 'emitir-nota-fiscal',
  );
  const needsInvoice =
    invoiceOption && (sale?.selectedOptions ?? []).includes(invoiceOption.id);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    setDepositPaidAt(today);
    setBalancePaidAt(today);
    setInvoiceNumber('');
    setCancellationReason('');
    attachmentsForm.reset({});
  }, [status, sale?.id]);

  if (!status || !needsTransitionModal(status)) return null;

  const depositAmount = sale?.depositAmount ?? 0;
  const balanceAmount = (sale?.total ?? 0) - depositAmount;
  const canConfirm =
    status === 'confirmed' ||
    status === 'canceled' ||
    !needsInvoice ||
    invoiceNumber.trim().length > 0;

  const handleConfirm = () => {
    if (status === 'confirmed') {
      onConfirm({ depositPaidAt });
      return;
    }

    if (status === 'canceled') {
      onConfirm({ cancellationReason });
      return;
    }

    const extra: Record<string, string> = { balancePaidAt };
    if (needsInvoice) {
      extra.invoiceNumber = invoiceNumber;
      const { invoicePdfPath, invoiceXmlPath } = attachmentsForm.getValues();
      if (invoicePdfPath) extra.invoicePdfPath = invoicePdfPath;
      if (invoiceXmlPath) extra.invoiceXmlPath = invoiceXmlPath;
    }
    onConfirm(extra);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {status === 'confirmed'
              ? 'Confirmar pagamento da entrada'
              : status === 'canceled'
                ? 'Motivo do cancelamento'
                : 'Concluir pedido'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {status === 'canceled' && (
            <Field>
              <FieldLabel>Motivo</FieldLabel>
              <Input
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
              />
            </Field>
          )}

          {status === 'confirmed' && (
            <>
              <div className="text-sm text-muted-foreground">
                Valor da entrada: <strong>R$ {depositAmount.toFixed(2)}</strong>
              </div>
              <Field>
                <FieldLabel>Data de pagamento da entrada</FieldLabel>
                <Input
                  type="date"
                  value={depositPaidAt}
                  onChange={(e) => setDepositPaidAt(e.target.value)}
                />
              </Field>
            </>
          )}

          {status === 'delivered' && (
            <>
              <div className="text-sm text-muted-foreground">
                Saldo a pagar: <strong>R$ {balanceAmount.toFixed(2)}</strong>
              </div>
              <Field>
                <FieldLabel>Data de pagamento do saldo</FieldLabel>
                <Input
                  type="date"
                  value={balancePaidAt}
                  onChange={(e) => setBalancePaidAt(e.target.value)}
                />
              </Field>

              {needsInvoice && (
                <>
                  <Field>
                    <FieldLabel>Número da nota fiscal</FieldLabel>
                    <Input
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      placeholder="Obrigatório"
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <FormUploader
                      name="invoicePdfPath"
                      control={attachmentsForm.control}
                      label="PDF da nota"
                      accept="application/pdf"
                      sm={12}
                      md={12}
                      lg={12}
                    />
                    <FormUploader
                      name="invoiceXmlPath"
                      control={attachmentsForm.control}
                      label="XML da nota"
                      accept=".xml,application/xml,text/xml"
                      sm={12}
                      md={12}
                      lg={12}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="success"
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
